import React, { useMemo, useRef, useState } from 'react';
import { codeFromScan, decodeImageFile, normalizeCode, submitPairing } from './pairDisplay';

// presto-qr-pairing (ADR 2026-09-03): "Pair ⧉" in the output-panel chrome.
//
// Both inputs are ALWAYS reachable; only their order changes. A phone gets the photo
// first, a desktop gets the code field first — because on a desktop `capture` degrades
// to a file picker with nothing useful to pick (the QR is on a physical screen across
// the room) and a built-in webcam faces the wrong way. Typing is not the lesser path:
// it is the same secret, and reading it still requires seeing the screen, so the
// line-of-sight second factor is intact either way.

const CODE_LEN = 8;

/** `XXXX-XXXX` as you type — the same grouping the screen prints, so the eye can
 *  checkpoint halfway through instead of tracking eight characters in one go. */
function grouped(code) {
  const c = normalizeCode(code).slice(0, CODE_LEN);
  return c.length > 4 ? `${c.slice(0, 4)}-${c.slice(4)}` : c;
}

export default function PairDisplayModal({ stationId, stationName, onClose }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  // A hint for ORDERING only, never a gate: a coarse pointer means a rear camera is
  // plausible. Misdetection costs nothing because both inputs are rendered regardless.
  const cameraFirst = useMemo(() => {
    try {
      return typeof window !== 'undefined' && window.matchMedia
        && window.matchMedia('(pointer: coarse)').matches;
    } catch (_) {
      return false;
    }
  }, []);

  const pair = async (raw) => {
    const normalized = codeFromScan(raw);
    if (normalized.length !== CODE_LEN) {
      setError('That code doesn\'t look right — check the screen and try again.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await submitPairing(normalized, stationId);
      setDone(res);
    } catch (e) {
      setError(e.message || 'Pairing failed.');
    } finally {
      setBusy(false);
    }
  };

  const onPhoto = async (ev) => {
    const file = ev.target.files && ev.target.files[0];
    ev.target.value = '';          // let the same photo be retried after a failed read
    if (!file) return;
    setBusy(true);
    setError('');
    let scanned = '';
    try {
      scanned = await decodeImageFile(file);
    } catch (_) {
      scanned = '';
    }
    setBusy(false);
    if (!scanned) {
      setError('Couldn\'t read a code in that photo. Try again, or type the code '
               + 'shown under it.');
      return;
    }
    await pair(scanned);
  };

  const photoBlock = (
    <div key="photo" style={{ marginBottom: '14px' }}>
      <button
        type="button"
        disabled={busy}
        onClick={() => fileRef.current && fileRef.current.click()}
        style={{
          width: '100%', padding: '11px 14px', border: 'none', borderRadius: '10px',
          background: '#0084ff', color: '#fff', fontWeight: 600, fontSize: '0.9rem',
          cursor: busy ? 'default' : 'pointer',
        }}
      >
        Take a photo of the code
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onPhoto}
        aria-label="Photograph the pairing code"
        style={{ display: 'none' }}
      />
    </div>
  );

  const typeBlock = (
    <div key="type" style={{ marginBottom: '14px' }}>
      <label htmlFor="pair-code" style={{
        display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '5px',
      }}>
        Or type the code shown on the screen
      </label>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          id="pair-code"
          value={grouped(code)}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') pair(code); }}
          placeholder="XXXX-XXXX"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          style={{
            flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: '10px',
            border: '1px solid #cbd5e1', fontSize: '1rem', letterSpacing: '0.12em',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}
        />
        <button
          type="button"
          disabled={busy || normalizeCode(code).length !== CODE_LEN}
          onClick={() => pair(code)}
          style={{
            padding: '10px 16px', border: 'none', borderRadius: '10px',
            background: normalizeCode(code).length === CODE_LEN ? '#0084ff' : '#e2e8f0',
            color: normalizeCode(code).length === CODE_LEN ? '#fff' : '#94a3b8',
            fontWeight: 600, cursor: busy ? 'default' : 'pointer',
          }}
        >
          Pair
        </button>
      </div>
    </div>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Pair a screen"
      style={{
        position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: '14px', padding: '18px', width: '100%',
        maxWidth: '380px', boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 style={{ margin: 0, fontSize: '1.05rem', color: '#0f172a' }}>Pair a screen</h2>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer',
                     color: '#64748b', lineHeight: 1 }}>×</button>
        </div>

        {done ? (
          <div style={{ marginTop: '14px', fontSize: '0.88rem', color: '#0f172a' }}>
            {/* The claim is held pending until someone taps the screen — saying "done"
                here would be a lie, and the user would walk away before confirming. */}
            <p style={{ margin: '0 0 8px' }}>
              Sent to <strong>{done.display || 'the screen'}</strong>.
            </p>
            <p style={{ margin: 0, color: '#475569' }}>
              Confirm on the screen itself to finish.
            </p>
          </div>
        ) : (
          <>
            <p style={{ margin: '10px 0 14px', fontSize: '0.82rem', color: '#475569' }}>
              On the screen, choose <strong>Set up this screen</strong> (or ask it to
              &ldquo;set up&rdquo; in chat). Then scan or type the code it shows to add
              <strong> {stationName || `station ${stationId}`}</strong> to it.
            </p>
            {cameraFirst ? [photoBlock, typeBlock] : [typeBlock, photoBlock]}
            {busy && (
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>Working…</p>
            )}
            {error && (
              <p role="alert" style={{ margin: 0, fontSize: '0.8rem', color: '#b91c1c' }}>
                {error}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
